import { useEffect, useMemo, useRef, useState } from "react";
import {
  FormGroup,
  HeaderRow,
  Left,
  Page,
  Right,
  RoleItem,
  RoleListContainer,
  SearchInput,
  Select,
} from "../styles/component.style";
import { BtnGeneral } from "../../../../styles/Buttons";
import { useCalculadorasDinamicas } from "../../../../hooks/calculadorasDinamicas/useCalculadorasDinamicas";
import {
  ActionCluster,
  AdvancedStack,
  AutoFillBox,
  Body,
  CatalogCard,
  CatalogItem,
  CatalogLayout,
  CheckboxRow,
  CodeChip,
  CodeChipRow,
  CodeDot,
  CodeEditorMain,
  CodeFooter,
  CodeGutter,
  CodeGutterLine,
  CodeHelpers,
  CodeShell,
  CodeTab,
  CodeTextarea,
  CodeTheme,
  CodeTopbar,
  CodeWindow,
  EmptyState,
  FieldBox,
  FieldCard,
  FieldCardMeta,
  FieldCardTitle,
  FieldCardTop,
  FieldHint,
  FieldLabel,
  FieldsLayout,
  FieldsList,
  Grid,
  HintBox,
  InlineSplit,
  Input,
  MainPanel,
  ModuleShell,
  PreviewField,
  PreviewFieldLabel,
  PreviewFieldMeta,
  PreviewFields,
  PreviewHeader,
  PreviewHero,
  PreviewMainValue,
  PreviewMini,
  PreviewResult,
  PreviewSecondary,
  PreviewSubtitle,
  PreviewTitle,
  ResultPresetCard,
  ResultPresetGrid,
  ResultPresetText,
  ResultPresetTitle,
  SectionCard,
  SectionCardGeneral,
  SelectInput,
  SourcePanel,
  StatusPill,
  Textarea,
  ToggleChip,
  ToggleWrap,
  TopBar,
  TopBarCopy,
  WizardTab,
  WizardTabsBar,
  WizardTabsHeader,
  ModalBackdrop,
  ModalBody,
  ModalClose,
  ModalContent,
  ModalHeader,
  ModalPreviewPanel,
  ModalTitle,
} from "./CalculadorasDinamicas.style";

const STEP_DEFINITIONS = [
  { shortLabel: "General", label: "Datos generales", description: "Nombre, estado y rangos." },
  { shortLabel: "Campos", label: "Campos", description: "Campos clínicos y controles." },
  {
    shortLabel: "Origen",
    label: "Origen del dato",
    description: "Manual, paciente, clínico o fijo.",
  },
  { shortLabel: "Resultado", label: "Resultado", description: "Cómo se devolverá la salida." },
  {
    shortLabel: "Lógica",
    label: "Lógica",
    description: "Funciones y operación técnica.",
    title:
      "Primero se definen los campos, luego su origen, después el resultado y al final la operación. Así el usuario funcional no mezcla negocio con código.",
  },
  { shortLabel: "Avanzado", label: "Avanzado", description: "Catálogo clínico y fuentes." },
];

const FIELD_TYPE_OPTIONS = [
  { value: "number", label: "Número" },
  { value: "text", label: "Texto corto" },
  { value: "select", label: "Lista" },
  { value: "date", label: "Fecha" },
  { value: "textarea", label: "Texto largo" },
  { value: "readonly", label: "Solo lectura" },
];

const SOURCE_MODE_OPTIONS = [
  { value: "manual", label: "Lo escribe el usuario" },
  { value: "patient", label: "Dato del paciente" },
  { value: "clinical", label: "Resultado clínico" },
  { value: "fixed", label: "Valor fijo" },
];

const PATIENT_SOURCE_OPTIONS = [
  { value: "patient.edad", label: "Edad del paciente" },
  { value: "patient.sexo", label: "Sexo del paciente" },
  { value: "patient.fechaNacimiento", label: "Fecha de nacimiento" },
  { value: "patient.peso", label: "Peso del paciente" },
  { value: "patient.talla", label: "Talla del paciente" },
];

const RESULT_PRESETS = [
  {
    id: "numeric",
    title: "Valor con unidad",
    description: "Ideal para TFG, IMC, dosis o salidas numéricas principales.",
    mode: "value_unit",
    label: "Resultado",
    sample_value: "24.91",
    unit: "",
    message: "",
    secondary_title: "Interpretación",
    secondary_value: "",
    color: "",
    help: "",
  },
  {
    id: "textual",
    title: "Mensaje final",
    description: "Útil cuando la calculadora entrega una recomendación o clasificación.",
    mode: "text",
    label: "Clasificación final",
    sample_value: "",
    unit: "",
    message: "Resultado dentro del rango esperado.",
    secondary_title: "",
    secondary_value: "",
    color: "",
    help: "",
  },
  {
    id: "double_card",
    title: "Tarjetas múltiples",
    description: "Muestra valor principal e interpretación al mismo tiempo.",
    mode: "cards",
    label: "Resultado",
    sample_value: "24.91",
    unit: "",
    message: "",
    secondary_title: "Clasificación",
    secondary_value: "Normal",
    color: "",
    help: "",
  },
];

const DEFAULT_RESULT_CONFIG = {
  preset: "numeric",
  mode: "value_unit",
  label: "Resultado",
  sample_value: "",
  unit: "",
  message: "",
  secondary_title: "Interpretación",
  secondary_value: "",
  color: "",
  help: "",
};

function resolveResultColor(value) {
  const normalized = String(value || "").trim();
  return /^#[0-9a-fA-F]{3,8}$/.test(normalized) ? normalized : "#0f172a";
}

export default function CalculadorasDinamicas() {
  const [leftOpen, setLeftOpen] = useState(true);
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedFieldIndex, setSelectedFieldIndex] = useState(0);
  const [codeCursor, setCodeCursor] = useState({ line: 1, column: 1 });
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const formulaRef = useRef(null);
  const gutterRef = useRef(null);

  const {
    items,
    search,
    setSearch,
    estadoFiltro,
    setEstadoFiltro,
    selected,
    form,
    setForm,
    sourceCatalog,
    selectedSourceId,
    sourceForm,
    setSourceForm,
    consecutivos,
    handleSelect,
    handleNew,
    handleSelectSource,
    handleNewSource,
    saveSource,
    addVariable,
    updateVariable,
    removeVariable,
    save,
  } = useCalculadorasDinamicas();

  const resultConfig = useMemo(
    () => ({ ...DEFAULT_RESULT_CONFIG, ...(form.result_config || {}) }),
    [form.result_config]
  );

  const variables = Array.isArray(form.variables) ? form.variables : [];
  const selectedVariable =
    variables[Math.min(selectedFieldIndex, Math.max(variables.length - 1, 0))] || null;
  const formulaLines = useMemo(
    () => Math.max(1, String(form.formula_js || "").split("\n").length),
    [form.formula_js]
  );
  const currentStepDefinition =
    STEP_DEFINITIONS[Math.min(currentStep, Math.max(STEP_DEFINITIONS.length - 1, 0))] ||
    STEP_DEFINITIONS[0];

  const visiblePreviewFields = useMemo(
    () =>
      variables.filter((variable) => Number(variable.visible ?? 1) === 1).map((variable) => {
        const layout = getLayoutConfig(variable);
        return {
          ...variable,
          placeholder: layout.placeholder || "",
          span: Number(layout.span) === 2 ? 2 : 1,
        };
      }),
    [variables]
  );

  const handleSelectCalculator = async (row) => {
    await handleSelect(row);
    setCurrentStep(0);
    setSelectedFieldIndex(0);
  };

  const handleNewCalculator = () => {
    handleNew();
    setCurrentStep(0);
    setSelectedFieldIndex(0);
    setCodeCursor({ line: 1, column: 1 });
  };

  const updateFormField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const updateResultConfig = (patch) => {
    setForm((prev) => ({
      ...prev,
      result_config: {
        ...DEFAULT_RESULT_CONFIG,
        ...(prev.result_config || {}),
        ...patch,
      },
    }));
  };

  const handleAddField = () => {
    const nextIndex = variables.length;
    addVariable();
    setSelectedFieldIndex(nextIndex);
    setCurrentStep(1);
  };

  const handleRemoveField = (index) => {
    removeVariable(index);
    setSelectedFieldIndex((prev) => Math.max(0, Math.min(prev, variables.length - 2)));
  };

  const updateFieldLayout = (index, patch) => {
    const nextLayout = {
      ...getLayoutConfig(variables[index]),
      ...patch,
    };
    updateVariable(index, { layout_json: JSON.stringify(nextLayout) });
  };

  const updateFieldOptions = (index, rawValue) => {
    const options = rawValue
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean)
      .map((item) => ({ label: item, value: item }));

    updateVariable(index, {
      options_json: options.length ? JSON.stringify(options) : "",
    });
  };

  const handleFieldLabelChange = (index, value) => {
    const currentKey = variables[index]?.variable_key || "";
    const nextKey =
      !currentKey || currentKey === buildVariableKey(variables[index]?.label || "")
        ? buildVariableKey(value)
        : currentKey;

    updateVariable(index, {
      label: value,
      variable_key: nextKey,
    });
  };

  const currentSourceMode = getSourceMode(selectedVariable);
  const sourceDetailOptions = getSourceDetailOptions(currentSourceMode, sourceCatalog);
  const sourceDetailValue = getSourceDetailValue(selectedVariable, currentSourceMode);
  const sourceIsAutomatic = currentSourceMode !== "manual";

  const handleSourceModeChange = (mode) => {
    if (!selectedVariable) return;

    const variable = selectedVariable;
    let patch = {};

    if (mode === "manual") {
      patch = {
        source_type: "manual",
        source_key: "manual.input",
        source_config_id: null,
        auto_fill: 0,
        prefill_mode: "none",
        refresh_strategy: "manual",
        allow_manual_override: 1,
        editable: 1,
        default_value: "",
      };
    } else if (mode === "patient") {
      patch = {
        source_type: "patient",
        source_key: PATIENT_SOURCE_OPTIONS[0].value,
        source_config_id: null,
        auto_fill: 1,
        prefill_mode: "initial",
        refresh_strategy: "on_open",
        allow_manual_override: Number(variable.allow_manual_override ?? 0),
      };
    } else if (mode === "clinical") {
      patch = {
        source_type: "api",
        source_key: "",
        source_config_id: sourceCatalog[0]?.id || null,
        auto_fill: 1,
        prefill_mode: "initial",
        refresh_strategy: "on_open",
        allow_manual_override:
          Number(sourceCatalog[0]?.allow_manual_override_default ?? 1) || 0,
      };
    } else if (mode === "fixed") {
      patch = {
        source_type: "constant",
        source_key: "constant.value",
        source_config_id: null,
        auto_fill: 1,
        prefill_mode: "initial",
        refresh_strategy: "on_open",
        allow_manual_override: 0,
      };
    }

    updateVariable(selectedFieldIndex, patch);
  };

  const handleSourceDetailChange = (value) => {
    if (!selectedVariable) return;

    if (currentSourceMode === "patient") {
      updateVariable(selectedFieldIndex, { source_key: value });
      return;
    }

    if (currentSourceMode === "clinical") {
      const selectedSource = sourceCatalog.find((source) => String(source.id) === String(value));
      updateVariable(selectedFieldIndex, {
        source_config_id: value ? Number(value) : null,
        allow_manual_override: Number(
          selectedSource?.allow_manual_override_default ??
            selectedVariable.allow_manual_override ??
            0
        ),
      });
    }
  };

  const handleLogicCursorUpdate = (element) => {
    if (!element) return;
    const beforeCursor = element.value.slice(0, element.selectionStart || 0);
    const line = beforeCursor.split("\n").length;
    const column = beforeCursor.length - beforeCursor.lastIndexOf("\n");
    setCodeCursor({ line, column });
  };

  const handleFormulaChange = (event) => {
    const nextValue = event.target.value;
    setForm((prev) => ({ ...prev, formula_js: nextValue }));
    handleLogicCursorUpdate(event.target);
  };

  const handleFormulaScroll = (event) => {
    if (gutterRef.current) {
      gutterRef.current.scrollTop = event.target.scrollTop;
    }
  };

  const insertLogicToken = (token) => {
    const editor = formulaRef.current;
    if (!editor) {
      updateFormField("formula_js", `${form.formula_js || ""}${token}`);
      return;
    }

    const start = editor.selectionStart || 0;
    const end = editor.selectionEnd || 0;
    const nextValue = `${form.formula_js?.slice(0, start) || ""}${token}${
      form.formula_js?.slice(end) || ""
    }`;

    setForm((prev) => ({ ...prev, formula_js: nextValue }));

    requestAnimationFrame(() => {
      editor.focus();
      const nextCursor = start + token.length;
      editor.setSelectionRange(nextCursor, nextCursor);
      handleLogicCursorUpdate(editor);
    });
  };

  const applyResultPreset = (presetId) => {
    const preset =
      RESULT_PRESETS.find((item) => item.id === presetId) || RESULT_PRESETS[0];
    updateResultConfig({
      preset: preset.id,
      mode: preset.mode,
      label: preset.label,
      sample_value: preset.sample_value,
      unit: preset.unit,
      message: preset.message,
      secondary_title: preset.secondary_title,
      secondary_value: preset.secondary_value,
      help: preset.help,
    });
  };

  const toggleCalculatorsPanel = () => {
    setLeftOpen((prev) => !prev);
  };

  useEffect(() => {
    if (!previewModalOpen) return;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setPreviewModalOpen(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [previewModalOpen]);

  return (
    <Page style={{ gap: leftOpen ? "18px" : "0px", transition: "gap 0.2s ease" }}>
      <Left
        style={{
          width: leftOpen ? undefined : "0px",
          minWidth: leftOpen ? undefined : "0px",
          maxWidth: leftOpen ? undefined : "0px",
          flex: leftOpen ? undefined : "0 0 0px",
          padding: leftOpen ? undefined : "0",
          border: leftOpen ? "1px solid #e2e8f0" : "none",
          background: leftOpen ? "#fff" : "transparent",
          overflow: "hidden",
          transition:
            "width 0.2s ease, min-width 0.2s ease, max-width 0.2s ease, flex 0.2s ease, padding 0.2s ease",
          position: "relative",
        }}
      >
        <div style={{ minWidth: "220px" }}>
          <HeaderRow>
            <h3 style={{ margin: 0, fontSize: "13px" }}>Calculadoras dinámicas</h3>
            <BtnGeneral
              bg="cyan"
              padding="4px 8px"
              radius="5px"
              fw="600"
              fs="11px"
              onClick={handleNewCalculator}
            >
              Nuevo
            </BtnGeneral>
          </HeaderRow>

          <FormGroup style={{ marginTop: "8px", marginBottom: "8px" }}>
            <Select
              value={estadoFiltro}
              onChange={(event) => setEstadoFiltro(event.target.value)}
              style={{ fontSize: "11px", paddingTop: "6px", paddingBottom: "6px" }}
            >
              <option value="">Todos</option>
              <option value="1">Activa</option>
              <option value="0">Inactiva</option>
            </Select>
          </FormGroup>

          <SearchInput
            placeholder="Buscar..."
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />

          <RoleListContainer>
            {items.map((item) => (
              <RoleItem
                key={item.id}
                active={selected === item.id}
                onClick={() => handleSelectCalculator(item)}
              >
                <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                  <strong style={{ fontSize: "12px" }}>{item.nombre}</strong>
                  <small style={{ color: "#64748b" }}>
                    V2 · {item.variables_count || 0} campos
                  </small>
                </div>
                <small>{Number(item.estado) === 1 ? "ACTIVA" : "INACTIVA"}</small>
              </RoleItem>
            ))}
          </RoleListContainer>
        </div>
      </Left>

      <div
        style={{
          flex: 1,
          minHeight: 0,
          position: "relative",
          display: "flex",
          flexDirection: "column",
          minWidth: 0,
        }}
      >
        <button
          onClick={toggleCalculatorsPanel}
          title={leftOpen ? "Ocultar panel" : "Mostrar panel"}
          style={collapseButtonStyle(leftOpen)}
        >
          {leftOpen ? "◀" : "▶"}
        </button>

        <Right
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            padding: 0,
            border: "none",
            borderRadius: 0,
            boxShadow: "none",
            background: "transparent",
          }}
        >
          <ModuleShell>
            <MainPanel>
              <WizardTabsHeader>
                <WizardTabsBar>
                  {STEP_DEFINITIONS.map((step, index) => (
                    <WizardTab
                      key={step.label}
                      type="button"
                      data-active={currentStep === index}
                      onClick={() => setCurrentStep(index)}
                      title={`${index + 1}. ${step.label}`}
                    >
                      {step.shortLabel || step.label}
                    </WizardTab>
                  ))}
                </WizardTabsBar>
              </WizardTabsHeader>

              <TopBar>
                <TopBarCopy>
                  <h2 title={currentStepDefinition?.title || undefined}>
                    {currentStepDefinition
                      ? `${currentStep + 1}. ${currentStepDefinition.label}`
                      : "Configuración"}
                  </h2>
                </TopBarCopy>
                <ActionCluster>
                  {currentStep === 1 && (
                    <BtnGeneral
                      bg="cyan"
                      padding="7px 14px"
                      radius="8px"
                      fw="700"
                      fs="11px"
                      onClick={handleAddField}
                    >
                      Agregar campo
                    </BtnGeneral>
                  )}
                  <BtnGeneral
                    bg="slategray"
                    padding="7px 14px"
                    radius="8px"
                    fw="700"
                    fs="11px"
                    onClick={() => setPreviewModalOpen(true)}
                  >
                    Vista previa
                  </BtnGeneral>
                  <BtnGeneral
                    bg="cyan"
                    padding="7px 14px"
                    radius="8px"
                    fw="600"
                    fs="11px"
                    onClick={handleNewCalculator}
                  >
                    Limpiar
                  </BtnGeneral>
                  <BtnGeneral
                    bg="green"
                    padding="7px 16px"
                    radius="8px"
                    fw="700"
                    fs="11px"
                    onClick={save}
                  >
                    Guardar
                  </BtnGeneral>
                </ActionCluster>
              </TopBar>

              <Body>
                {currentStep === 0 && (
                  <SectionCardGeneral>
                    <Grid>
                      <FieldBox>
                        <FieldLabel>Nombre</FieldLabel>
                        <Input
                          value={form.nombre || ""}
                          onChange={(event) => updateFormField("nombre", event.target.value)}
                          placeholder="Ej: IMC (Índice de Masa Corporal)"
                        />
                      </FieldBox>

                      <FieldBox>
                        <FieldLabel>Estado</FieldLabel>
                        <SelectInput
                          value={String(form.estado ?? 1)}
                          onChange={(event) =>
                            updateFormField("estado", Number(event.target.value))
                          }
                        >
                          <option value="1">1 activa</option>
                          <option value="0">0 inactiva</option>
                        </SelectInput>
                      </FieldBox>

                      <FieldBox>
                        <FieldLabel>Sexo aplica</FieldLabel>
                        <SelectInput
                          value={form.sexo_aplica || "A"}
                          onChange={(event) =>
                            updateFormField("sexo_aplica", event.target.value)
                          }
                        >
                          <option value="A">Ambos</option>
                          <option value="F">Femenino</option>
                          <option value="M">Masculino</option>
                        </SelectInput>
                      </FieldBox>

                      <FieldBox>
                        <FieldLabel>Orden visual</FieldLabel>
                        <Input
                          value={String(form.orden_visual ?? 0)}
                          onChange={(event) =>
                            updateFormField("orden_visual", event.target.value)
                          }
                        />
                      </FieldBox>

                      <FieldBox>
                        <FieldLabel>Edad mínima</FieldLabel>
                        <Input
                          value={String(form.edad_min ?? 0)}
                          onChange={(event) => updateFormField("edad_min", event.target.value)}
                        />
                      </FieldBox>

                      <FieldBox>
                        <FieldLabel>Edad máxima</FieldLabel>
                        <Input
                          value={String(form.edad_max ?? 200)}
                          onChange={(event) => updateFormField("edad_max", event.target.value)}
                        />
                      </FieldBox>

                      <FieldBox $full>
                        <FieldLabel>Descripción</FieldLabel>
                        <Textarea
                          value={form.descripcion || ""}
                          onChange={(event) =>
                            updateFormField("descripcion", event.target.value)
                          }
                          $minHeight="86px"
                          placeholder="Explica brevemente para qué sirve esta calculadora."
                        />
                      </FieldBox>

                    </Grid>
                  </SectionCardGeneral>
                )}

                {currentStep === 1 && (
                  <SectionCard>
                    {variables.length ? (
                      <FieldsLayout>
                        <FieldsList>
                          {variables.map((variable, index) => (
                            <FieldCard
                              key={`${variable.variable_key || "new"}-${index}`}
                              type="button"
                              $active={selectedFieldIndex === index}
                              onClick={() => setSelectedFieldIndex(index)}
                            >
                              <FieldCardTop>
                                <FieldCardTitle>
                                  {variable.label || `Campo ${index + 1}`}
                                </FieldCardTitle>
                                <BtnGeneral
                                  bg="slategray"
                                  padding="3px 10px"
                                  radius="8px"
                                  fw="700"
                                  fs="10px"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    handleRemoveField(index);
                                  }}
                                >
                                  Quitar
                                </BtnGeneral>
                              </FieldCardTop>
                              <FieldCardMeta>
                                {getFieldTypeLabel(variable.field_type)} · {getSourceModeLabel(getSourceMode(variable))}
                              </FieldCardMeta>
                              <FieldCardMeta>
                                Clave: {variable.variable_key || "pendiente"} · Orden {index + 1}
                              </FieldCardMeta>
                            </FieldCard>
                          ))}
                        </FieldsList>

                        <div style={{ minWidth: 0 }}>
                          {selectedVariable ? (
                            <Grid>
                              <FieldBox>
                                <FieldLabel>Nombre del campo</FieldLabel>
                                <Input
                                  value={selectedVariable.label || ""}
                                  onChange={(event) =>
                                    handleFieldLabelChange(selectedFieldIndex, event.target.value)
                                  }
                                  placeholder="Ej: Creatinina"
                                />
                              </FieldBox>

                              <FieldBox>
                                <FieldLabel>Clave interna</FieldLabel>
                                <Input
                                  value={selectedVariable.variable_key || ""}
                                  onChange={(event) =>
                                    updateVariable(selectedFieldIndex, {
                                      variable_key: buildVariableKey(event.target.value),
                                    })
                                  }
                                  placeholder="creatinina"
                                />
                              </FieldBox>

                              <FieldBox>
                                <FieldLabel>Tipo de campo</FieldLabel>
                                <SelectInput
                                  value={selectedVariable.field_type || "text"}
                                  onChange={(event) =>
                                    updateVariable(selectedFieldIndex, {
                                      field_type: event.target.value,
                                    })
                                  }
                                >
                                  {FIELD_TYPE_OPTIONS.map((option) => (
                                    <option key={option.value} value={option.value}>
                                      {option.label}
                                    </option>
                                  ))}
                                </SelectInput>
                              </FieldBox>

                              <FieldBox>
                                <FieldLabel>Ancho visual</FieldLabel>
                                <SelectInput
                                  value={String(getLayoutConfig(selectedVariable).span || 1)}
                                  onChange={(event) =>
                                    updateFieldLayout(selectedFieldIndex, {
                                      span: Number(event.target.value),
                                    })
                                  }
                                >
                                  <option value="1">Mitad</option>
                                  <option value="2">Completo</option>
                                </SelectInput>
                              </FieldBox>

                              <FieldBox $full>
                                <FieldLabel>Placeholder</FieldLabel>
                                <Input
                                  value={getLayoutConfig(selectedVariable).placeholder || ""}
                                  onChange={(event) =>
                                    updateFieldLayout(selectedFieldIndex, {
                                      placeholder: event.target.value,
                                    })
                                  }
                                  placeholder="Ej: 1.73 m o 170 cm"
                                />
                              </FieldBox>

                              {selectedVariable.field_type === "select" && (
                                <FieldBox $full>
                                  <FieldLabel>Opciones</FieldLabel>
                                  <Input
                                    value={getOptionsText(selectedVariable)}
                                    onChange={(event) =>
                                      updateFieldOptions(selectedFieldIndex, event.target.value)
                                    }
                                    placeholder="Ej: mg/dL, mmol/L"
                                  />
                                  <FieldHint>Sepáralas por coma. El sistema las convertirá a JSON.</FieldHint>
                                </FieldBox>
                              )}

                              <FieldBox $full>
                                <FieldLabel>Reglas del campo</FieldLabel>
                                <ToggleWrap>
                                  <ToggleChip $checked={Number(selectedVariable.required) === 1}>
                                    <input
                                      type="checkbox"
                                      checked={Number(selectedVariable.required) === 1}
                                      onChange={(event) =>
                                        updateVariable(selectedFieldIndex, {
                                          required: event.target.checked ? 1 : 0,
                                        })
                                      }
                                    />
                                    Obligatorio
                                  </ToggleChip>
                                  <ToggleChip $checked={Number(selectedVariable.visible) === 1}>
                                    <input
                                      type="checkbox"
                                      checked={Number(selectedVariable.visible) === 1}
                                      onChange={(event) =>
                                        updateVariable(selectedFieldIndex, {
                                          visible: event.target.checked ? 1 : 0,
                                        })
                                      }
                                    />
                                    Visible
                                  </ToggleChip>
                                  <ToggleChip $checked={Number(selectedVariable.editable) === 1}>
                                    <input
                                      type="checkbox"
                                      checked={Number(selectedVariable.editable) === 1}
                                      onChange={(event) =>
                                        updateVariable(selectedFieldIndex, {
                                          editable: event.target.checked ? 1 : 0,
                                        })
                                      }
                                    />
                                    Editable
                                  </ToggleChip>
                                </ToggleWrap>
                              </FieldBox>
                            </Grid>
                          ) : null}
                        </div>
                      </FieldsLayout>
                    ) : (
                      <EmptyState>
                        Aún no hay campos creados. Empieza agregando el primero.
                      </EmptyState>
                    )}
                  </SectionCard>
                )}

                {currentStep === 2 && (
                  <SectionCard>
                    {selectedVariable ? (
                      <SourcePanel>
                        <Grid>
                          <FieldBox>
                            <FieldLabel>Campo a configurar</FieldLabel>
                            <SelectInput
                              value={String(selectedFieldIndex)}
                              onChange={(event) =>
                                setSelectedFieldIndex(Number(event.target.value))
                              }
                            >
                              {variables.map((variable, index) => (
                                <option key={`${variable.variable_key || "field"}-${index}`} value={index}>
                                  {variable.label || `Campo ${index + 1}`}
                                </option>
                              ))}
                            </SelectInput>
                          </FieldBox>

                          <FieldBox>
                            <FieldLabel>De dónde sale el dato</FieldLabel>
                            <SelectInput
                              value={currentSourceMode}
                              onChange={(event) => handleSourceModeChange(event.target.value)}
                            >
                              {SOURCE_MODE_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </SelectInput>
                          </FieldBox>

                          {currentSourceMode !== "fixed" && (
                            <FieldBox>
                              <FieldLabel>Detalle</FieldLabel>
                              <SelectInput
                                value={sourceDetailValue}
                                onChange={(event) =>
                                  handleSourceDetailChange(event.target.value)
                                }
                              >
                                {sourceDetailOptions.map((option) => (
                                  <option key={option.value} value={option.value}>
                                    {option.label}
                                  </option>
                                ))}
                              </SelectInput>
                            </FieldBox>
                          )}

                          {currentSourceMode === "fixed" && (
                            <FieldBox>
                              <FieldLabel>Valor fijo</FieldLabel>
                              <Input
                                value={selectedVariable.default_value || ""}
                                onChange={(event) =>
                                  updateVariable(selectedFieldIndex, {
                                    default_value: event.target.value,
                                  })
                                }
                                placeholder="Escribe el valor fijo"
                              />
                            </FieldBox>
                          )}
                        </Grid>

                        <AutoFillBox>
                          <CheckboxRow $disabled={!sourceIsAutomatic}>
                            <input
                              type="checkbox"
                              checked={sourceIsAutomatic}
                              disabled
                              readOnly
                            />
                            Autollenar al abrir
                          </CheckboxRow>
                          <CheckboxRow>
                            <input
                              type="checkbox"
                              checked={Number(selectedVariable.allow_manual_override) === 1}
                              onChange={(event) =>
                                updateVariable(selectedFieldIndex, {
                                  allow_manual_override: event.target.checked ? 1 : 0,
                                })
                              }
                              disabled={currentSourceMode === "fixed"}
                            />
                            Permitir modificar el valor sugerido
                          </CheckboxRow>
                        </AutoFillBox>

                        <HintBox>
                          {currentSourceMode === "clinical"
                            ? "En 'Resultado clínico' solo se muestran nombres funcionales como 'Creatinina'. El sistema asume por defecto que toma el último resultado disponible."
                            : currentSourceMode === "patient"
                            ? "El sistema tomará este dato del contexto del paciente seleccionado al abrir la calculadora."
                            : currentSourceMode === "fixed"
                            ? "Úsalo para constantes o valores que no dependen del paciente."
                            : "Si el dato es manual, el usuario lo diligencia directamente en la calculadora."}
                        </HintBox>
                      </SourcePanel>
                    ) : (
                      <EmptyState>
                        Primero crea al menos un campo en el paso anterior.
                      </EmptyState>
                    )}
                  </SectionCard>
                )}

                {currentStep === 3 && (
                  <SectionCard>
                    <ResultPresetGrid>
                      {RESULT_PRESETS.map((preset) => (
                        <ResultPresetCard
                          key={preset.id}
                          type="button"
                          $active={resultConfig.preset === preset.id}
                          onClick={() => applyResultPreset(preset.id)}
                        >
                          <ResultPresetTitle>{preset.title}</ResultPresetTitle>
                          <ResultPresetText>{preset.description}</ResultPresetText>
                        </ResultPresetCard>
                      ))}
                    </ResultPresetGrid>

                    <Grid>
                      <FieldBox>
                        <FieldLabel>Título del resultado</FieldLabel>
                        <Input
                          value={resultConfig.label}
                          onChange={(event) =>
                            updateResultConfig({ label: event.target.value })
                          }
                        />
                      </FieldBox>

                      {resultConfig.mode !== "text" && (
                        <>
                          <FieldBox>
                            <FieldLabel>Valor de ejemplo</FieldLabel>
                            <Input
                              value={resultConfig.sample_value}
                              onChange={(event) =>
                                updateResultConfig({ sample_value: event.target.value })
                              }
                            />
                          </FieldBox>

                          <FieldBox>
                            <FieldLabel>Unidad</FieldLabel>
                            <Input
                              value={resultConfig.unit}
                              onChange={(event) =>
                                updateResultConfig({ unit: event.target.value })
                              }
                              placeholder="Opcional"
                            />
                          </FieldBox>
                        </>
                      )}

                      {resultConfig.mode === "text" && (
                        <FieldBox $full>
                          <FieldLabel>Mensaje final</FieldLabel>
                          <Input
                            value={resultConfig.message}
                            onChange={(event) =>
                              updateResultConfig({ message: event.target.value })
                            }
                          />
                        </FieldBox>
                      )}

                      {resultConfig.mode === "cards" && (
                        <>
                          <FieldBox>
                            <FieldLabel>Título secundario</FieldLabel>
                            <Input
                              value={resultConfig.secondary_title}
                              onChange={(event) =>
                                updateResultConfig({
                                  secondary_title: event.target.value,
                                })
                              }
                            />
                          </FieldBox>

                          <FieldBox>
                            <FieldLabel>Valor secundario</FieldLabel>
                            <Input
                              value={resultConfig.secondary_value}
                              onChange={(event) =>
                                updateResultConfig({
                                  secondary_value: event.target.value,
                                })
                              }
                            />
                          </FieldBox>
                        </>
                      )}

                      <FieldBox $full>
                        <FieldLabel>Texto de apoyo</FieldLabel>
                        <Input
                          value={resultConfig.help}
                          onChange={(event) =>
                            updateResultConfig({ help: event.target.value })
                          }
                          placeholder="Contexto clínico, límites o aclaraciones para el usuario."
                        />
                      </FieldBox>

                      <FieldBox>
                        <FieldLabel>Color del resultado</FieldLabel>
                        <Input
                          value={resultConfig.color || ""}
                          onChange={(event) =>
                            updateResultConfig({ color: event.target.value })
                          }
                          placeholder="#0f172a"
                        />
                      </FieldBox>
                    </Grid>
                  </SectionCard>
                )}

                {currentStep === 4 && (
                  <SectionCard>
                    {/* <HintBox>
                      Primero se definen los campos, luego su origen, después el resultado y al
                      final la operación. Así el usuario funcional no mezcla negocio con código.
                    </HintBox> */}

                    <CodeShell>
                      <CodeTopbar>
                        <CodeWindow>
                          <CodeDot $color="#f38ba8" />
                          <CodeDot $color="#f9e2af" />
                          <CodeDot $color="#a6e3a1" />
                        </CodeWindow>
                        <CodeTab>
                          <span>logica-calculadora.js</span>
                          <small>JavaScript</small>
                        </CodeTab>
                        <CodeTheme>Tema: Catppuccin Mocha</CodeTheme>
                      </CodeTopbar>

                      <CodeHelpers>
                        <strong>Campos disponibles en la lógica</strong>
                        <CodeChipRow>
                          {variables.map((variable, index) => (
                            <CodeChip
                              key={`${variable.variable_key || "field"}-${index}`}
                              type="button"
                              onClick={() =>
                                insertLogicToken(
                                  `data.${variable.variable_key || buildVariableKey(variable.label || `campo_${index + 1}`)}`
                                )
                              }
                            >
                              data.
                              {variable.variable_key ||
                                buildVariableKey(variable.label || `campo_${index + 1}`)}
                            </CodeChip>
                          ))}
                        </CodeChipRow>
                      </CodeHelpers>

                      <CodeEditorMain>
                        <CodeGutter ref={gutterRef}>
                          {Array.from({ length: formulaLines }).map((_, index) => (
                            <CodeGutterLine key={`line-${index + 1}`}>
                              {index + 1}
                            </CodeGutterLine>
                          ))}
                        </CodeGutter>
                        <CodeTextarea
                          ref={formulaRef}
                          value={form.formula_js || ""}
                          onChange={handleFormulaChange}
                          onClick={(event) => handleLogicCursorUpdate(event.target)}
                          onKeyUp={(event) => handleLogicCursorUpdate(event.target)}
                          onSelect={(event) => handleLogicCursorUpdate(event.target)}
                          onScroll={handleFormulaScroll}
                        />
                      </CodeEditorMain>

                      <CodeFooter>
                        <span>
                          <strong>Tip:</strong> devuelve un objeto con `label`, `value`, `unit` y,
                          si aplica, `secondaryLabel` y `secondaryValue`.
                        </span>
                        <span>
                          UTF-8 · JavaScript · Ln {codeCursor.line}, Col {codeCursor.column}
                        </span>
                      </CodeFooter>
                    </CodeShell>
                  </SectionCard>
                )}

                {currentStep === 5 && (
                  <SectionCard>
                    <AdvancedStack>
                      <CatalogLayout>
                        <CatalogCard>
                          <InlineSplit>
                            <FieldLabel>Fuentes clínicas</FieldLabel>
                            <BtnGeneral
                              bg="cyan"
                              padding="5px 12px"
                              radius="8px"
                              fw="700"
                              fs="10px"
                              onClick={handleNewSource}
                            >
                              Nueva fuente
                            </BtnGeneral>
                          </InlineSplit>

                          {sourceCatalog.length ? (
                            sourceCatalog.map((source) => (
                              <CatalogItem
                                key={source.id}
                                type="button"
                                $active={selectedSourceId === source.id}
                                onClick={() => handleSelectSource(source.id)}
                              >
                                <strong style={{ fontSize: "12px" }}>{source.nombre}</strong>
                                <span style={{ fontSize: "11px", color: "#64748b" }}>
                                  {source.codigo} · cons {source.consecutivo_id}
                                </span>
                              </CatalogItem>
                            ))
                          ) : (
                            <EmptyState>No hay fuentes clínicas configuradas todavía.</EmptyState>
                          )}
                        </CatalogCard>

                        <CatalogCard>
                          <InlineSplit>
                            <FieldLabel>
                              {sourceForm.id ? "Editar fuente" : "Nueva fuente"}
                            </FieldLabel>
                            <BtnGeneral
                              bg="green"
                              padding="5px 12px"
                              radius="8px"
                              fw="700"
                              fs="10px"
                              onClick={saveSource}
                            >
                              Guardar fuente
                            </BtnGeneral>
                          </InlineSplit>

                          <Grid>
                            <FieldBox>
                              <FieldLabel>Código técnico</FieldLabel>
                              <Input
                                value={sourceForm.codigo || ""}
                                onChange={(event) =>
                                  setSourceForm((prev) => ({
                                    ...prev,
                                    codigo: event.target.value,
                                  }))
                                }
                                placeholder="creatinina"
                              />
                            </FieldBox>

                            <FieldBox>
                              <FieldLabel>Nombre funcional</FieldLabel>
                              <Input
                                value={sourceForm.nombre || ""}
                                onChange={(event) =>
                                  setSourceForm((prev) => ({
                                    ...prev,
                                    nombre: event.target.value,
                                  }))
                                }
                                placeholder="Creatinina"
                              />
                            </FieldBox>

                            <FieldBox>
                              <FieldLabel>Consecutivo</FieldLabel>
                              <SelectInput
                                value={String(sourceForm.consecutivo_id || "")}
                                onChange={(event) =>
                                  setSourceForm((prev) => ({
                                    ...prev,
                                    consecutivo_id: event.target.value || "",
                                  }))
                                }
                              >
                                <option value="">Selecciona un consecutivo</option>
                                {consecutivos.map((item) => (
                                  <option key={item.id} value={item.Consecutivo}>
                                    {item.Api} · {item.Consecutivo}
                                  </option>
                                ))}
                              </SelectInput>
                            </FieldBox>

                            <FieldBox>
                              <FieldLabel>Tipo dato</FieldLabel>
                              <SelectInput
                                value={sourceForm.tipo_dato || "number"}
                                onChange={(event) =>
                                  setSourceForm((prev) => ({
                                    ...prev,
                                    tipo_dato: event.target.value,
                                  }))
                                }
                              >
                                <option value="number">Número</option>
                                <option value="text">Texto</option>
                                <option value="date">Fecha</option>
                              </SelectInput>
                            </FieldBox>

                            <FieldBox>
                              <FieldLabel>Orden</FieldLabel>
                              <Input
                                value={String(sourceForm.orden ?? 0)}
                                onChange={(event) =>
                                  setSourceForm((prev) => ({
                                    ...prev,
                                    orden: event.target.value,
                                  }))
                                }
                              />
                            </FieldBox>

                            <FieldBox>
                              <FieldLabel>Api route</FieldLabel>
                              <Input
                                value={sourceForm.api_route || ""}
                                onChange={(event) =>
                                  setSourceForm((prev) => ({
                                    ...prev,
                                    api_route: event.target.value,
                                  }))
                                }
                                placeholder="patients/result-latest"
                              />
                            </FieldBox>

                            <FieldBox>
                              <FieldLabel>Campo respuesta</FieldLabel>
                              <Input
                                value={sourceForm.response_field || ""}
                                onChange={(event) =>
                                  setSourceForm((prev) => ({
                                    ...prev,
                                    response_field: event.target.value,
                                  }))
                                }
                                placeholder="Data_APIM1"
                              />
                            </FieldBox>

                            <FieldBox $full>
                              <FieldLabel>Descripción</FieldLabel>
                              <Textarea
                                value={sourceForm.descripcion || ""}
                                onChange={(event) =>
                                  setSourceForm((prev) => ({
                                    ...prev,
                                    descripcion: event.target.value,
                                  }))
                                }
                                $minHeight="72px"
                              />
                            </FieldBox>

                            <FieldBox $full>
                              <FieldLabel>Reglas de la fuente</FieldLabel>
                              <ToggleWrap>
                                <ToggleChip $checked={Number(sourceForm.estado) === 1}>
                                  <input
                                    type="checkbox"
                                    checked={Number(sourceForm.estado) === 1}
                                    onChange={(event) =>
                                      setSourceForm((prev) => ({
                                        ...prev,
                                        estado: event.target.checked ? 1 : 0,
                                      }))
                                    }
                                  />
                                  Activa
                                </ToggleChip>
                                <ToggleChip
                                  $checked={
                                    Number(sourceForm.allow_manual_override_default) === 1
                                  }
                                >
                                  <input
                                    type="checkbox"
                                    checked={
                                      Number(sourceForm.allow_manual_override_default) === 1
                                    }
                                    onChange={(event) =>
                                      setSourceForm((prev) => ({
                                        ...prev,
                                        allow_manual_override_default: event.target.checked
                                          ? 1
                                          : 0,
                                      }))
                                    }
                                  />
                                  Permite override por defecto
                                </ToggleChip>
                              </ToggleWrap>
                            </FieldBox>
                          </Grid>
                        </CatalogCard>
                      </CatalogLayout>

                      <HintBox>
                        Administra aquí únicamente las fuentes clínicas reutilizables de la V2.
                      </HintBox>
                    </AdvancedStack>
                  </SectionCard>
                )}
              </Body>
            </MainPanel>

            {previewModalOpen && (
              <ModalBackdrop onClick={() => setPreviewModalOpen(false)}>
                <ModalContent onClick={(event) => event.stopPropagation()}>
                  <ModalHeader>
                    <ModalTitle>Vista previa</ModalTitle>
                    <ModalClose type="button" onClick={() => setPreviewModalOpen(false)}>
                      Cerrar
                    </ModalClose>
                  </ModalHeader>

                  <ModalBody>
                    <ModalPreviewPanel>
                      <PreviewHeader>
                        <div>
                          <h3>Vista previa</h3>
                          <p>Así se vería la calculadora operativa para el usuario final.</p>
                        </div>
                        <StatusPill $active={Number(form.estado) === 1}>
                          {Number(form.estado) === 1 ? "Activa" : "Inactiva"}
                        </StatusPill>
                      </PreviewHeader>

                      <PreviewHero>
                        <PreviewMini>Paciente seleccionado</PreviewMini>
                        <PreviewTitle>{form.nombre || "Nueva calculadora"}</PreviewTitle>
                        <PreviewSubtitle>
                          {getSexoPreview(form.sexo_aplica)} · {form.edad_min || 0} a{" "}
                          {form.edad_max || 200} años
                        </PreviewSubtitle>
                      </PreviewHero>

                      <PreviewFields>
                        {visiblePreviewFields.length ? (
                          visiblePreviewFields.map((field, index) => (
                            <PreviewField
                              key={`${field.variable_key || "preview"}-${index}`}
                              $full={field.span === 2}
                            >
                              <PreviewFieldLabel>
                                {field.label || `Campo ${index + 1}`}
                              </PreviewFieldLabel>
                              <PreviewFieldMeta>
                                {field.placeholder ||
                                  getPreviewSourceText(field, sourceCatalog) ||
                                  "Campo visible para el usuario"}
                              </PreviewFieldMeta>
                            </PreviewField>
                          ))
                        ) : (
                          <PreviewField $full>
                            <PreviewFieldLabel>Aún no hay campos</PreviewFieldLabel>
                            <PreviewFieldMeta>
                              Cuando agregues campos en el paso 2, se verán aquí.
                            </PreviewFieldMeta>
                          </PreviewField>
                        )}
                      </PreviewFields>

                      <PreviewResult>
                        <strong>{resultConfig.label || "Resultado"}</strong>
                        {resultConfig.mode === "text" ? (
                          <PreviewMainValue
                            $color={resolveResultColor(resultConfig.color)}
                            style={{ fontSize: "16px", lineHeight: 1.5 }}
                          >
                            {resultConfig.message || "Mensaje final del cálculo"}
                          </PreviewMainValue>
                        ) : (
                          <PreviewMainValue $color={resolveResultColor(resultConfig.color)}>
                            {resultConfig.sample_value || "0.00"}
                            {resultConfig.unit ? ` ${resultConfig.unit}` : ""}
                          </PreviewMainValue>
                        )}

                        {resultConfig.mode === "cards" && (
                          <PreviewSecondary>
                            <PreviewFieldLabel>
                              {resultConfig.secondary_title || "Interpretación"}
                            </PreviewFieldLabel>
                            <PreviewFieldMeta>
                              {resultConfig.secondary_value || "Sin valor secundario"}
                            </PreviewFieldMeta>
                          </PreviewSecondary>
                        )}

                        <small>
                          {resultConfig.help ||
                            "La salida se adapta al tipo de resultado configurado en el paso 4."}
                        </small>
                      </PreviewResult>
                    </ModalPreviewPanel>
                  </ModalBody>
                </ModalContent>
              </ModalBackdrop>
            )}
          </ModuleShell>
        </Right>
      </div>
    </Page>
  );
}

function collapseButtonStyle(leftOpen) {
  return {
    position: "absolute",
    left: leftOpen ? "-12px" : "0px",
    top: "50%",
    transform: "translateY(-50%)",
    background: "#fff",
    border: "1px solid #cbd5e1",
    borderRadius: "50%",
    width: "24px",
    height: "24px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    color: "#475569",
    fontSize: "12px",
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
    zIndex: 10,
    transition: "left 0.2s ease",
  };
}

function safeParseJson(value, fallback) {
  if (!value) return fallback;
  if (typeof value === "object") return value;

  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" ? parsed : fallback;
  } catch (error) {
    return fallback;
  }
}

function getLayoutConfig(variable) {
  return safeParseJson(variable?.layout_json, {});
}

function getOptionsText(variable) {
  const options = safeParseJson(variable?.options_json, []);
  if (!Array.isArray(options)) {
    return "";
  }

  return options
    .map((option) => option?.label || option?.value || "")
    .filter(Boolean)
    .join(", ");
}

function buildVariableKey(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .replace(/^[0-9]/, "")
    .toLowerCase();
}

function getFieldTypeLabel(fieldType) {
  return (
    FIELD_TYPE_OPTIONS.find((option) => option.value === fieldType)?.label || "Texto corto"
  );
}

function getSourceMode(variable) {
  if (!variable) return "manual";
  if (variable.source_type === "api") return "clinical";
  if (variable.source_type === "constant") return "fixed";
  if (variable.source_type === "patient") return "patient";
  return "manual";
}

function getSourceModeLabel(mode) {
  return SOURCE_MODE_OPTIONS.find((option) => option.value === mode)?.label || "Manual";
}

function getSourceDetailOptions(mode, sourceCatalog) {
  if (mode === "patient") return PATIENT_SOURCE_OPTIONS;
  if (mode === "clinical") {
    return sourceCatalog.map((source) => ({
      value: String(source.id),
      label: source.nombre,
    }));
  }
  if (mode === "manual") {
    return [{ value: "manual.input", label: "Digitación manual" }];
  }
  return [];
}

function getSourceDetailValue(variable, mode) {
  if (!variable) return "";
  if (mode === "clinical") return String(variable.source_config_id || "");
  if (mode === "patient") return variable.source_key || PATIENT_SOURCE_OPTIONS[0].value;
  if (mode === "manual") return "manual.input";
  return "";
}

function getSexoPreview(value) {
  if (value === "F") return "Aplica para mujeres";
  if (value === "M") return "Aplica para hombres";
  return "Aplica para ambos sexos";
}

function getPreviewSourceText(variable, sourceCatalog) {
  const mode = getSourceMode(variable);
  if (mode === "manual") return "Digitación manual";
  if (mode === "patient") {
    return (
      PATIENT_SOURCE_OPTIONS.find((option) => option.value === variable.source_key)?.label ||
      "Dato del paciente"
    );
  }
  if (mode === "clinical") {
    return (
      sourceCatalog.find((source) => Number(source.id) === Number(variable.source_config_id))
        ?.nombre || "Resultado clínico"
    );
  }
  if (mode === "fixed") {
    return `Valor fijo: ${variable.default_value || "pendiente"}`;
  }
  return "";
}
